import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React, { AllHTMLAttributes, ComponentProps, FC, ReactHTML, Ref, createElement } from 'react';
import styles from './TextStack.module.scss';

import {
  ContentType,
  ExternalLink,
  InternalLink,
  LinkVariant,
  Topic,
} from 'api/contentful/contentTypes.cloverdotcom';
import { ButtonLink } from 'components/Button/ButtonLink';
import { ButtonLinkV2 } from 'components/Button/ButtonLinkV2';
import { PricingMarketingHook } from 'components/Pricing/PricingMarketingHook/PricingMarketingHook';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { TextV2 } from 'components/TextV2/TextV2';
import { flattenContentfulData } from 'services/cms/flattenContentful';
import { componentKey } from 'utils/EntryUtil';
import { ContentLink } from '../ContentLink/ContentLink';
import { Blurb } from '../Contentful/Blurb';
import { Text } from '../Contentful/Text';
import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import { MaterialIcon } from '../MaterialIcon/MaterialIcon';

export interface ITextStackProps<E extends HTMLElement = HTMLDivElement> extends AllHTMLAttributes<E> {
  entry?: ReferencedEntry<Topic>;
  tagName?: keyof ReactHTML;
  titleTagName?: keyof ReactHTML;
  titleClassName?: string;
  subTitleClassName?: string;
  blurbClassName?: string;
  linkClassName?: string;
  linkContainerClassName?: string;
  showLinkArrow?: boolean;
  innerRef?: Ref<E>;
  forceLinkAsSpan?: boolean;
  isButton?: boolean;
  buttonSize?: ComponentProps<typeof ButtonLinkV2>['size'];
}

const TextStack: FC<ITextStackProps<HTMLDivElement>> = ({
  entry,
  tagName,
  titleTagName,
  titleClassName,
  subTitleClassName,
  blurbClassName,
  linkClassName,
  linkContainerClassName,
  showLinkArrow,
  innerRef,
  forceLinkAsSpan,
  tabIndex,
  isButton,
  buttonSize,
  ...props
}) => {
  if (!entry || !entry.fields) return null;

  const {
    link: defaultLink,
    secondaryLinks: moreLinks,
    dcsEntities,
    titleMaxWidth,
    richBlurb,
  } = entry.fields;
  const secondaryLinks = moreLinks ?? [];
  const links = [defaultLink, ...secondaryLinks];

  const arrow = showLinkArrow && (
    <div className={styles.linkArrow}>
      <MaterialIcon icon="keyboard_arrow_right" />
    </div>
  );

  const toButtonEntry = (cta: ReferencedEntry<InternalLink | ExternalLink>) => {
    return {
      contentType: cta?.sys?.contentType?.sys?.id,
      title: cta?.fields?.title,
      ...cta,
    };
  };

  return createElement(
    tagName || 'div',
    { ...props, ref: innerRef },
    <>
      <TextV2 value={entry.fields?.subTitle} variant="modern-eyebrow" className={subTitleClassName} />
      <Text
        tagName={titleTagName || 'h2'}
        entry={entry}
        field="title"
        className={titleClassName}
        style={{ maxWidth: titleMaxWidth }}
      />
      {richBlurb ? (
        <RichBlurb document={richBlurb} className={blurbClassName} />
      ) : (
        <Blurb entry={entry} field="blurb" className={cx(styles.blurb, blurbClassName)} />
      )}
      {!!dcsEntities?.fields?.bundleId && <PricingMarketingHook dcsEntities={dcsEntities.fields} />}
      <div className={cx(styles.ctaContainer, linkContainerClassName)}>
        {links?.map((link, index) => {
          const buttonEntry = link ? toButtonEntry(link) : undefined;
          const isTextVariant = buttonEntry?.fields?.variant === LinkVariant.TEXT;
          const isSecondary = buttonEntry?.fields?.variant === LinkVariant.SECONDARY;
          const contentLinkClassname = !isTextVariant ? linkClassName : '';
          if (!link) return null;
          const key = componentKey(link, index);
          const isModalLink = link?.sys?.contentType?.sys.id === ContentType.MODAL_LINK;

          if (isButton && isModalLink) {
            return (
              <ButtonLink
                entry={buttonEntry}
                className={contentLinkClassname}
                tabIndex={tabIndex}
                key={key}
                isTextVariant={isTextVariant}
              >
                {arrow}
              </ButtonLink>
            );
          }

          if (isButton) {
            return (
              <ButtonLinkV2
                key={key}
                className={linkClassName}
                entry={flattenContentfulData(buttonEntry)}
                size={buttonSize}
                isButton={isButton}
              />
            );
          }

          return forceLinkAsSpan ? (
            <Text key={key} entry={buttonEntry} field="title" className={linkClassName}>
              {arrow}
            </Text>
          ) : (
            <ContentLink
              key={key}
              entry={buttonEntry}
              className={cx(contentLinkClassname, styles.link, {
                [styles.linkPrimary]: !isSecondary,
                [styles.linkSecondary]: isSecondary,
                [styles.linkText]: isTextVariant,
              })}
              tabIndex={tabIndex}
            >
              {arrow}
            </ContentLink>
          );
        })}
      </div>
    </>,
  );
};
const Wrapped = applyEntryDataAttributes(TextStack);
export { Wrapped as TextStack };
